import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import {
  Microphone, MicrophoneSlash, VideoCamera, VideoCameraSlash, SpeakerHigh,
  ChatCircleDots, SignOut, UserMinus, MagicWand, Smiley, X
} from "@phosphor-icons/react";
import ShareGate from "@/components/ShareGate";
import ArtistCameraStage from "@/components/ArtistCameraStage";

const EMOJI_LIST = ["🔥", "🎶", "❤️", "🎤", "🌟", "👏", "🤘", "✨", "💃", "😍"];

const ConcertLive = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [concert, setConcert] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [floatingEmojis, setFloatingEmojis] = useState([]);
  const [showSpectators, setShowSpectators] = useState(false);
  const [spectators, setSpectators] = useState([]);
  const [access, setAccess] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [liveSnapshot, setLiveSnapshot] = useState(null);
  const [controls, setControls] = useState({
    mic: true, camera: true, dolby: true, ai_fx: true, volume: 80,
  });
  const lastTsRef = useRef(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    // Stable cleanup pattern: only re-run when user/id changes; api & nav are module/stable refs.
    if (!user) { nav("/login", { state: { from: `/live/${id}` } }); return; }
    api.get(`/concerts/${id}`).then((r) => setConcert(r.data));
    api.get(`/concerts/${id}/access`).then((r) => setAccess(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  const refreshAccess = () => api.get(`/concerts/${id}/access`).then((r) => setAccess(r.data));

  // poll messages every 2s
  useEffect(() => {
    if (!concert) return;
    const tick = async () => {
      try {
        const { data } = await api.get(`/concerts/${id}/chat`, {
          params: lastTsRef.current ? { since: lastTsRef.current, limit: 50 } : { limit: 50 },
        });
        if (data.length) {
          setMessages((prev) => [...prev, ...data].slice(-100));
          lastTsRef.current = data[data.length - 1].created_at;
          data.forEach((m) => {
            if (m.emoji) pushEmoji(m.emoji);
          });
        }
      } catch (e) {
        if (process.env.NODE_ENV === "development") console.warn("chat poll error", e);
      }
    };
    tick();
    const int = setInterval(tick, 2200);
    return () => clearInterval(int);
  }, [concert, id]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Fan: poll live snapshot every 2s
  useEffect(() => {
    if (!concert || !user) return;
    if (user.id === concert.artist_id) return;  // artist already sees own camera
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/concerts/${id}/snapshot`);
        if (!cancelled && data?.data_url) setLiveSnapshot(data.data_url);
      } catch (err) {
        if (process.env.NODE_ENV === "development") console.warn("snapshot fetch failed", err);
      }
    };
    tick();
    const int = setInterval(tick, 2200);
    return () => { cancelled = true; clearInterval(int); };
  }, [concert, user, id]);

  const pushEmoji = (e) => {
    const id = Math.random().toString(36).slice(2);
    setFloatingEmojis((p) => [...p, { id, emoji: e, left: 10 + Math.random() * 80 }]);
    setTimeout(() => setFloatingEmojis((p) => p.filter((x) => x.id !== id)), 3600);
  };

  const sendMessage = async (emoji = null) => {
    const payload = { text: emoji ? emoji : text.trim(), emoji: emoji || null };
    if (!payload.text) return;
    try {
      const { data } = await api.post(`/concerts/${id}/chat`, payload);
      setMessages((p) => [...p, data].slice(-100));
      lastTsRef.current = data.created_at;
      if (emoji) pushEmoji(emoji);
      if (!emoji) setText("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Message refusé");
    }
  };

  const goLive = async () => {
    await api.post(`/concerts/${id}/go-live`);
    toast.success("🔴 Vous êtes en direct !");
    setCameraActive(true);  // auto-démarre la caméra
    const { data } = await api.get(`/concerts/${id}`);
    setConcert(data);
  };

  const endLive = async () => {
    await api.post(`/concerts/${id}/end`);
    toast.success("Live terminé. Revenus crédités.");
    setCameraActive(false);  // coupe la caméra
    const { data } = await api.get(`/concerts/${id}`);
    setConcert(data);
  };

  const loadSpectators = async () => {
    const { data } = await api.get(`/concerts/${id}/spectators`);
    setSpectators(data);
    setShowSpectators(true);
  };

  const kickUser = async (uid) => {
    await api.post(`/concerts/${id}/kick`, { user_id: uid, reason: "Comportement inapproprié" });
    toast.success("Spectateur retiré");
    loadSpectators();
  };

  if (!concert) return <div className="min-h-screen bg-black text-white p-10">Chargement...</div>;
  const isArtist = user?.id === concert.artist_id;

  // Share gate for non-artist viewers
  if (!isArtist && access && access.has_ticket && !access.has_shared) {
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        <div className="flex items-center justify-between p-4 border-b border-white/5 glass">
          <div className="flex items-center gap-3">
            <Link to={`/concert/${id}`} className="text-zinc-400 hover:text-white"><X size={20} /></Link>
            <div>
              <h1 className="font-display font-bold text-lg">{concert.title}</h1>
              <p className="text-xs text-zinc-500">{concert.artist_name}</p>
            </div>
          </div>
        </div>
        <div className="max-w-xl mx-auto px-6 py-12">
          <p className="text-xs uppercase tracking-[0.3em] text-[#F59E0B] font-bold mb-3">Dernière étape</p>
          <h2 className="font-display font-black text-3xl tracking-tight mb-6">
            Plus on est de fans, plus c'est beau.
          </h2>
          <ShareGate
            concertId={id}
            concertTitle={concert.title}
            artistName={concert.artist_name}
            onUnlocked={refreshAccess}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between p-4 border-b border-white/5 glass">
        <div className="flex items-center gap-3">
          <Link to={`/concert/${id}`} data-testid="live-back-btn" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 text-zinc-300 hover:text-white hover:bg-white/5 transition-colors text-sm">
            <X size={16} /> Retour
          </Link>
          <div>
            <div className="flex items-center gap-2">
              {concert.is_live && <span className="px-2 py-0.5 rounded bg-red-600 text-xs font-bold animate-pulse">LIVE</span>}
              <h1 className="font-display font-bold text-lg">{concert.title}</h1>
            </div>
            <p className="text-xs text-zinc-500">{concert.artist_name} • {concert.attendees_count} spectateurs</p>
          </div>
        </div>
        {isArtist && (
          <div className="flex gap-2">
            {!concert.is_live ? (
              <Button data-testid="live-go-live" size="sm" className="bg-red-600 hover:bg-red-700" onClick={goLive}>
                Démarrer le live
              </Button>
            ) : (
              <Button data-testid="live-end" size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10 hover:text-white" onClick={endLive}>
                Terminer
              </Button>
            )}
            <Button data-testid="live-spectators" size="sm" variant="ghost" onClick={loadSpectators}>
              Spectateurs
            </Button>
          </div>
        )}
      </div>

      <div className="flex-1 grid lg:grid-cols-[1fr_360px] gap-0">
        {/* Stage */}
        <div className="relative bg-gradient-to-b from-[#1a0007] to-black overflow-hidden">
          <div className="absolute inset-0 grain" aria-hidden />
          <div className="relative z-10 h-full min-h-[60vh] flex flex-col items-center justify-center p-8">
            <div className="w-full max-w-2xl aspect-video rounded-2xl border border-white/10 bg-black/80 overflow-hidden relative">
              {/* Cover background */}
              <img src={concert.cover_image_url || "https://images.pexels.com/photos/10168223/pexels-photo-10168223.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"}
                alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />

              {/* Artist USB camera live preview */}
              {isArtist && <ArtistCameraStage active={cameraActive} onActiveChange={setCameraActive} concertId={id} />}

              {/* Fan view: live snapshot streaming */}
              {!isArtist && liveSnapshot && (
                <img
                  data-testid="fan-live-snapshot"
                  src={liveSnapshot}
                  alt="Live concert"
                  className="absolute inset-0 w-full h-full object-cover z-10"
                />
              )}

              <div className="relative z-10 h-full flex items-end p-6">
                <div>
                  {concert.is_live ? (
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
                      <span className="text-sm font-mono uppercase tracking-widest">Streaming Dolby HD</span>
                    </div>
                  ) : (
                    <span className="text-sm text-zinc-300 font-mono uppercase tracking-widest">En attente de l'artiste...</span>
                  )}
                  <p className="font-display font-black text-3xl">{concert.artist_name}</p>
                </div>
              </div>
              {/* Floating emojis */}
              <AnimatePresence>
                {floatingEmojis.map((f) => (
                  <motion.div
                    key={f.id}
                    initial={{ y: 0, opacity: 0, scale: 0.5 }}
                    animate={{ y: -260, opacity: 1, scale: 1.4 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 3.5, ease: "easeOut" }}
                    className="absolute bottom-4 text-4xl pointer-events-none"
                    style={{ left: `${f.left}%` }}
                  >
                    {f.emoji}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Controls (artist only - but visible to all in demo) */}
            <div className="mt-6 flex flex-wrap items-center gap-3 p-4 rounded-2xl glass">
              <Button data-testid="ctrl-mic" size="icon" variant={controls.mic ? "default" : "outline"}
                className={controls.mic ? "bg-[#E11D48] hover:bg-[#BE123C]" : "border-white/20"}
                onClick={() => setControls(c => ({ ...c, mic: !c.mic }))}>
                {controls.mic ? <Microphone weight="fill" /> : <MicrophoneSlash weight="fill" />}
              </Button>
              <Button data-testid="ctrl-cam" size="icon" variant={controls.camera ? "default" : "outline"}
                className={controls.camera ? "bg-[#E11D48] hover:bg-[#BE123C]" : "border-white/20"}
                onClick={() => setControls(c => ({ ...c, camera: !c.camera }))}>
                {controls.camera ? <VideoCamera weight="fill" /> : <VideoCameraSlash weight="fill" />}
              </Button>
              <div className="flex items-center gap-2 px-3 py-1 rounded-lg border border-white/10">
                <SpeakerHigh size={18} />
                <Slider data-testid="ctrl-volume" className="w-24" value={[controls.volume]} max={100}
                  onValueChange={(v) => setControls(c => ({ ...c, volume: v[0] }))} />
                <span className="text-xs font-mono w-8">{controls.volume}</span>
              </div>
              <Button data-testid="ctrl-dolby" size="sm" variant="outline"
                className={controls.dolby ? "border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B]/10 hover:text-[#F59E0B]" : "border-white/20 text-white hover:bg-white/10 hover:text-white"}
                onClick={() => setControls(c => ({ ...c, dolby: !c.dolby }))}>
                Dolby {controls.dolby ? "ON" : "OFF"}
              </Button>
              <Button data-testid="ctrl-aifx" size="sm" variant="outline"
                className={controls.ai_fx ? "border-[#E11D48] text-[#E11D48] hover:bg-[#E11D48]/10 hover:text-[#E11D48]" : "border-white/20 text-white hover:bg-white/10 hover:text-white"}
                onClick={() => setControls(c => ({ ...c, ai_fx: !c.ai_fx }))}>
                <MagicWand weight="fill" className="mr-1" /> IA FX {controls.ai_fx ? "ON" : "OFF"}
              </Button>
            </div>
          </div>
        </div>

        {/* Chat sidebar */}
        <aside className="border-l border-white/5 bg-[#080808] flex flex-col h-[80vh] lg:h-auto">
          <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
            <ChatCircleDots className="text-[#E11D48]" />
            <span className="font-bold text-sm uppercase tracking-widest">Chat live</span>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-2 min-h-[300px]" data-testid="chat-messages">
            {messages.length === 0 && <p className="text-center text-zinc-500 text-sm mt-10">Soyez le premier à envoyer un message !</p>}
            {messages.map((m) => (
              <div key={m.id} className={`flex gap-2 ${m.is_artist ? "items-start" : ""}`}>
                <div className={`px-3 py-2 rounded-2xl text-sm max-w-[85%] ${m.is_artist ? "bg-[#E11D48]/20 border border-[#E11D48]/40" : "bg-white/5 border border-white/5"}`}>
                  <div className={`text-xs font-bold mb-0.5 ${m.is_artist ? "text-[#F59E0B]" : "text-zinc-400"}`}>
                    {m.user_name} {m.is_artist && "★"}
                  </div>
                  <div className={m.emoji ? "text-3xl" : ""}>{m.text}</div>
                </div>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
          <div className="p-3 border-t border-white/5 space-y-2">
            <div className="flex flex-wrap gap-1">
              {EMOJI_LIST.map((e) => (
                <button
                  key={e}
                  data-testid={`chat-emoji-${e}`}
                  onClick={() => sendMessage(e)}
                  className="w-8 h-8 rounded-lg hover:bg-white/10 text-lg transition-colors"
                >{e}</button>
              ))}
            </div>
            <div className="flex gap-2">
              <Input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                placeholder="Écrire un message..."
                className="bg-black/40 border-white/10 text-white" />
              <Button data-testid="chat-send" onClick={() => sendMessage()} className="bg-[#E11D48] hover:bg-[#BE123C]">
                Envoyer
              </Button>
            </div>
          </div>
        </aside>
      </div>

      {/* Spectators modal */}
      {showSpectators && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur z-50 flex items-center justify-center p-6" onClick={() => setShowSpectators(false)}>
          <div className="bg-[#0f0f10] border border-white/10 rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-xl">Spectateurs ({spectators.length})</h3>
              <button onClick={() => setShowSpectators(false)} className="text-zinc-400 hover:text-white"><X /></button>
            </div>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {spectators.map((s) => (
                <div key={s.user_id} className="flex items-center justify-between p-3 rounded-lg bg-black/30 border border-white/5">
                  <div>
                    <div className="font-bold text-sm">{s.name} {s.kicked && <span className="text-xs text-red-400">(retiré)</span>}</div>
                    <div className="text-xs text-zinc-500">{s.email} • {s.amount_paid > 0 ? `${s.amount_paid}€` : "gratuit"}</div>
                  </div>
                  {!s.kicked && (
                    <Button data-testid={`kick-${s.user_id}`} size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-400" onClick={() => kickUser(s.user_id)}>
                      <UserMinus size={14} className="mr-1" /> Retirer
                    </Button>
                  )}
                </div>
              ))}
              {spectators.length === 0 && <p className="text-zinc-500 text-center py-6">Pas encore de spectateurs.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConcertLive;
